import { Component, inject } from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { NavigationEnd, Router, RouterLink, Routes } from '@angular/router';
import { LinkComponent } from '../link/link.component';
import { combineLatest, filter, map, Observable, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { BreadcrumbService } from '../../state/breadcrumb/breadcrumb.service';

@Component({
  selector: 'app-breadcrumbs',
  imports: [IconComponent, RouterLink, LinkComponent, AsyncPipe, ButtonComponent],
  templateUrl: './breadcrumbs.component.html',
  styleUrl: './breadcrumbs.component.css',
})
export class BreadcrumbsComponent {
  private router = inject(Router);
  private breadcrumbMapper = inject(BreadcrumbService);
  private routeLabels: { [key: string]: string };
  protected path: Observable<{ label: string; url: string }[]>;
  private navigationHistory: string[] = [];

  constructor() {
    this.routeLabels = this.createRouteLabels(this.router.config);
    const segments$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      tap((event) => this.navigationHistory.push(event.urlAfterRedirects)),
      map((event) => event.urlAfterRedirects.split(/[?#]/)[0].split('/').slice(1)),
    );
    const labelsMap$ = this.breadcrumbMapper.getLabels();

    this.path = combineLatest([segments$, labelsMap$]).pipe(
      map(([segments, labelsMap]) =>
        segments.map((segment, index) => ({
          label: labelsMap[segment] || this.routeLabels[segment] || segment,
          url: '/' + segments.slice(0, index + 1).join('/'),
        })),
      ),
    );
  }

  private createRouteLabels(routes: Routes): { [key: string]: string } {
    const labels: { [key: string]: string } = {};

    for (const route of routes) {
      const label = route.data?.['breadcrumb'];
      const segment = route.path?.split('/').find((part) => part && !part.startsWith(':') && part !== '**');

      if (typeof label === 'string' && segment) {
        labels[segment] = label;
      }

      Object.assign(labels, this.createRouteLabels(route.children ?? []));
    }

    return labels;
  }

  goBack() {
    if (this.navigationHistory.length > 1) {
      this.navigationHistory.pop();
      this.router.navigate([this.navigationHistory.pop()]);
    } else {
      this.router.navigate(['/home']);
    }
  }
}
